import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { keyframes } from "@mui/system";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ViewStreamRoundedIcon from "@mui/icons-material/ViewStreamRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";

import Dialog from "../../../components/dialog/Dialog";
import Content from "../../../components/Content";
import { CreateSubjectModal } from "../../../modals/general/CreateSubjectModal";
import { GetFacilities } from "../../../hooks/useFacilityData";
import { GetUsers } from "../../../hooks/useUserData";
import { GetUserGroupsByOrganisationId } from "../../../hooks/useUserGroupdata";
import { FacilityEditModal } from "./FacilityEditView";

const floatOrb = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(-14px, 20px, 0) scale(1.04); }
`;

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";
const NAVY = "#10324a";

const AVATAR_GRADIENTS = [
  ["#10324a", "#2d5a7e"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#8a1125", "#c23b54"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const gradientFor = (name) =>
  AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];

const initialsFor = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const FacilityAvatar = ({ name, size = 46 }) => {
  const [a, b] = gradientFor(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "14px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: `0 1px 0 rgba(255,255,255,0.4) inset, 0 10px 22px -12px ${a}55`,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "14px",
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.22), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.34,
          letterSpacing: "-0.02em",
          color: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initialsFor(name)}
      </Typography>
    </Box>
  );
};

const MetaChip = ({ icon: Icon, label, tone = "default" }) => {
  const styles =
    tone === "brand"
      ? {
          border: "rgba(138,17,37,0.32)",
          bg: "rgba(138,17,37,0.08)",
          fg: BRAND,
          dot: BRAND,
          dotHalo: "rgba(138,17,37,0.18)",
        }
      : tone === "navy"
      ? {
          border: "rgba(16,50,74,0.32)",
          bg: "rgba(16,50,74,0.08)",
          fg: NAVY,
          dot: NAVY,
          dotHalo: "rgba(16,50,74,0.18)",
        }
      : {
          border: "rgba(10,22,40,0.12)",
          bg: "rgba(10,22,40,0.025)",
          fg: "rgba(95,104,119,0.75)",
          dot: "#c7c3ba",
          dotHalo: "transparent",
        };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.75,
        height: 32,
        borderRadius: "999px",
        border: `1px solid ${styles.border}`,
        background: styles.bg,
        color: styles.fg,
        fontSize: "13px",
        fontWeight: 700,
        letterSpacing: "0.005em",
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: styles.dot,
          boxShadow: `0 0 0 4px ${styles.dotHalo}`,
          flexShrink: 0,
        }}
      />
      {Icon && <Icon sx={{ fontSize: 16, opacity: 0.95, flexShrink: 0 }} />}
      {label}
    </Box>
  );
};

const MetricCell = ({ label, value, total, tint, index, suffix }) => {
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  return (
    <Box
      sx={{
        py: 3.5,
        pr: 3,
        pl: { xs: 0, md: index === 0 ? 0 : 3 },
        position: "relative",
        borderRight: {
          xs: "none",
          md: index < 3 ? `1px solid ${LINE}` : "none",
        },
        borderBottom: {
          xs: index < 2 ? `1px solid ${LINE}` : "none",
          md: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: INK_MUTED,
          mb: 1.5,
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: "2.75rem",
            fontWeight: 500,
            lineHeight: 0.95,
            letterSpacing: "-0.035em",
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
          {suffix && (
            <Box component="span" sx={{ fontSize: "0.55em", color: INK_MUTED, ml: 0.25 }}>
              {suffix}
            </Box>
          )}
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "2px",
          width: "100%",
          maxWidth: 180,
          background: LINE_SOFT,
          borderRadius: "2px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: tint,
            transformOrigin: "left",
            transform: `scaleX(${pct})`,
          }}
        />
      </Box>
    </Box>
  );
};

const FacilityRow = ({ facility, onSelect }) => (
  <Box
    onClick={() => onSelect(facility.id)}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "auto 1fr auto",
        md: "auto minmax(0, 1.6fr) minmax(0, 0.8fr) minmax(0, 0.8fr) auto",
      },
      alignItems: "center",
      gap: { xs: 2, md: 3 },
      px: 3,
      py: 2.5,
      cursor: "pointer",
      position: "relative",
      borderBottom: `1px solid ${LINE_SOFT}`,
      transition: "background 0.3s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "16%",
        bottom: "16%",
        width: "2px",
        background: BRAND,
        transform: "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        background: "rgba(138, 17, 37, 0.025)",
        "&::before": { transform: "scaleY(1)" },
        "& .row-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
        "& .row-name": { color: BRAND },
      },
    }}
  >
    <FacilityAvatar name={facility.facility_name || "?"} size={48} />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        className="row-name"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "17px",
          fontWeight: 600,
          color: INK,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          transition: "color 0.25s ease",
        }}
      >
        {facility.facility_name}
      </Typography>
      <Typography
        sx={{
          fontSize: "12.5px",
          color: INK_MUTED,
          mt: 0.25,
          fontFamily: '"DM Sans", sans-serif',
          letterSpacing: "0.01em",
        }}
      >
        Anläggning
      </Typography>
    </Box>
    <Box sx={{ display: { xs: "none", md: "flex" }, minWidth: 0 }}>
      <MetaChip
        icon={PeopleAltRoundedIcon}
        label={`${facility.userCount} användare`}
        tone={facility.userCount > 0 ? "brand" : "default"}
      />
    </Box>
    <Box sx={{ display: { xs: "none", md: "flex" }, minWidth: 0 }}>
      <MetaChip
        icon={GroupsRoundedIcon}
        label={`${facility.groupCount} ${facility.groupCount === 1 ? "grupp" : "grupper"}`}
        tone={facility.groupCount > 0 ? "navy" : "default"}
      />
    </Box>
    <Box
      className="row-arrow"
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `1px solid ${LINE}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0,
        transform: "translateX(-6px)",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        color: BRAND,
      }}
    >
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </Box>
  </Box>
);

const FacilityCard = ({ facility, onSelect }) => (
  <Box
    onClick={() => onSelect(facility.id)}
    sx={{
      position: "relative",
      p: 3.5,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "16px",
      cursor: "pointer",
      overflow: "hidden",
      transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "48px",
        height: "2px",
        background: BRAND,
        transform: "scaleX(0)",
        transformOrigin: "left",
        transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        borderColor: "rgba(10,22,40,0.18)",
        transform: "translateY(-3px)",
        boxShadow:
          "0 24px 48px -24px rgba(10,22,40,0.16), 0 2px 6px -2px rgba(10,22,40,0.04)",
        "&::before": { transform: "scaleX(1)" },
        "& .card-arrow": { transform: "translateX(3px)", color: BRAND },
      },
    }}
  >
    <Box sx={{ mb: 3 }}>
      <FacilityAvatar name={facility.facility_name || "?"} size={56} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "22px",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.02em",
        lineHeight: 1.2,
        mb: 0.5,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        minHeight: "53px",
      }}
    >
      {facility.facility_name}
    </Typography>
    <Typography
      sx={{
        fontSize: "12.5px",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
        mb: 3,
      }}
    >
      Anläggning
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
      <MetaChip
        icon={PeopleAltRoundedIcon}
        label={`${facility.userCount} användare`}
        tone={facility.userCount > 0 ? "brand" : "default"}
      />
      <MetaChip
        icon={GroupsRoundedIcon}
        label={`${facility.groupCount} ${facility.groupCount === 1 ? "grupp" : "grupper"}`}
        tone={facility.groupCount > 0 ? "navy" : "default"}
      />
    </Stack>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 2.5,
        borderTop: `1px solid ${LINE_SOFT}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: INK_MUTED,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        Redigera
      </Typography>
      <ArrowForwardRoundedIcon
        className="card-arrow"
        sx={{
          fontSize: 18,
          color: INK_MUTED,
          transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </Box>
  </Box>
);

const EmptyState = ({ onCreate, hasQuery }) => (
  <Box
    sx={{
      py: 10,
      textAlign: "center",
      border: `1px dashed ${LINE}`,
      borderRadius: "16px",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,248,245,0.6))",
    }}
  >
    <Box
      sx={{
        width: 68,
        height: 68,
        mx: "auto",
        mb: 3,
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 20px 40px -20px rgba(16,50,74,0.4)",
      }}
    >
      <ApartmentRoundedIcon sx={{ color: "#fff", fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "1.75rem",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.025em",
        mb: 1,
      }}
    >
      {hasQuery ? "Inga träffar" : "Inga anläggningar ännu"}
    </Typography>
    <Typography
      sx={{
        fontSize: "14.5px",
        color: INK_MUTED,
        maxWidth: 420,
        mx: "auto",
        mb: 4,
        lineHeight: 1.6,
      }}
    >
      {hasQuery
        ? "Prova en annan sökterm för att se fler anläggningar."
        : "Skapa din första anläggning för att börja koppla grupper och användare."}
    </Typography>
    {!hasQuery && (
      <Box
        component="button"
        type="button"
        onClick={onCreate}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 3,
          py: 1.5,
          background: BRAND,
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.25s ease",
          boxShadow: "0 10px 24px -12px rgba(138,17,37,0.5)",
          "&:hover": {
            background: "#6b0d1d",
            transform: "translateY(-1px)",
          },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 18 }} />
        Skapa din första anläggning
      </Box>
    )}
  </Box>
);

export const FacilityView = () => {
  const [organisationId, setOrganisationId] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editFacilityId, setEditFacilityId] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("list");

  const { data: facilityList, isLoading } = GetFacilities();
  const { data: usersData } = GetUsers();
  const { data: userGroupList } = GetUserGroupsByOrganisationId(organisationId);

  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const { organisation_id } = JSON.parse(userData);
        setOrganisationId(organisation_id);
      }
    } catch (e) {
      console.error("Error getting organisation id:", e);
    }
  }, []);

  const facilities = useMemo(() => {
    const raw = Array.isArray(facilityList?.data) ? facilityList.data : [];
    const users = Array.isArray(usersData?.data) ? usersData.data : [];
    const groups = Array.isArray(userGroupList?.data) ? userGroupList.data : [];

    const userCountByFacility = users.reduce((acc, u) => {
      const fid = u.facility?.id || u.facility_id;
      if (fid != null) acc[fid] = (acc[fid] || 0) + 1;
      return acc;
    }, {});

    const groupCountByFacility = groups.reduce((acc, g) => {
      const fid = g.facility_id;
      if (fid != null) acc[fid] = (acc[fid] || 0) + 1;
      return acc;
    }, {});

    return raw.map((f) => ({
      id: f.id,
      organisation_id: f.organisation_id,
      facility_name: f.facility_name,
      userCount: userCountByFacility[f.id] || 0,
      groupCount: groupCountByFacility[f.id] || 0,
    }));
  }, [facilityList, usersData, userGroupList]);

  const metrics = useMemo(() => {
    let totalUsers = 0;
    let totalGroups = 0;
    let covered = 0;
    for (const f of facilities) {
      totalUsers += f.userCount;
      totalGroups += f.groupCount;
      if (f.userCount > 0) covered += 1;
    }
    const total = facilities.length;
    const coverage = total > 0 ? Math.round((covered / total) * 100) : 0;
    return { total, totalUsers, totalGroups, coverage };
  }, [facilities]);

  const filteredFacilities = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return facilities;
    return facilities.filter((f) =>
      (f.facility_name || "").toLowerCase().includes(q)
    );
  }, [facilities, search]);

  const closeEditModal = () => setEditFacilityId(null);
  const hasActiveQuery = !!search.trim();

  return (
    <Content>
      <Box
        sx={{
          position: "relative",
          px: { xs: 1, md: 2 },
          pt: 2,
          pb: 6,
          minHeight: "100%",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(138,17,37,0.09), rgba(138,17,37,0) 62%)",
            filter: "blur(8px)",
            pointerEvents: "none",
            animation: `${floatOrb} 14s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 80,
            left: -140,
            width: 460,
            height: 460,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(16,50,74,0.07), rgba(16,50,74,0) 62%)",
            filter: "blur(10px)",
            pointerEvents: "none",
            animation: `${floatOrb} 18s ease-in-out infinite reverse`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, mb: 5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: "1.5px",
                background: `linear-gradient(90deg, ${BRAND}, rgba(138,17,37,0))`,
              }}
            />
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: BRAND,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Organisation · Fastighetsregister
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  color: INK,
                  mb: 2.5,
                }}
              >
                Anläggningar
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontWeight: 300,
                    fontStyle: "italic",
                    fontSize: "0.58em",
                    color: BRAND,
                    mt: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  & struktur
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  color: INK_MUTED,
                  maxWidth: 560,
                }}
              >
                Organisera era fysiska platser och koppla dem till grupper och
                användare. Varje anläggning bildar en enhet som ger överblick
                över åtkomst och tillhörighet.
              </Typography>
            </Box>

            <Box
              component="button"
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                px: 2.75,
                py: 1.625,
                background: INK,
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.005em",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                boxShadow:
                  "0 14px 32px -14px rgba(10,22,40,0.45), 0 2px 4px -2px rgba(10,22,40,0.08)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${BRAND}, transparent 70%)`,
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    "0 22px 44px -18px rgba(10,22,40,0.55), 0 2px 6px -2px rgba(10,22,40,0.12)",
                  "&::before": { opacity: 1 },
                },
                "& > *": { position: "relative", zIndex: 1 },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 18 }} />
              Ny anläggning
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              borderTop: `1px solid ${LINE}`,
              borderBottom: `1px solid ${LINE}`,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0))",
              backdropFilter: "blur(4px)",
            }}
          >
            <MetricCell
              label="Totalt antal anläggningar"
              value={metrics.total}
              total={metrics.total}
              tint={INK}
              index={0}
            />
            <MetricCell
              label="Anslutna användare"
              value={metrics.totalUsers}
              total={metrics.total}
              tint={BRAND}
              index={1}
            />
            <MetricCell
              label="Kopplade grupper"
              value={metrics.totalGroups}
              total={metrics.total}
              tint={NAVY}
              index={2}
            />
            <MetricCell
              label="Täckning"
              value={metrics.coverage}
              total={100}
              tint="#2b8c4d"
              index={3}
              suffix="%"
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              height: 44,
              minWidth: 280,
              flex: { xs: "1 1 100%", md: "0 1 340px" },
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              transition: "all 0.25s ease",
              "&:focus-within": {
                borderColor: "rgba(10,22,40,0.28)",
                boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
              },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 18, color: INK_MUTED }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök anläggningar..."
              sx={{
                flex: 1,
                fontSize: "14px",
                fontFamily: '"DM Sans", sans-serif',
                color: INK,
                "& input::placeholder": {
                  color: INK_MUTED,
                  opacity: 1,
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px !important",
                px: 1.5,
                py: 0.75,
                color: INK_MUTED,
                transition: "all 0.25s ease",
                "&.Mui-selected": {
                  background: INK,
                  color: "#fff",
                  "&:hover": { background: INK },
                },
              },
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewStreamRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <FilterAltRoundedIcon sx={{ fontSize: 14, color: INK_MUTED }} />
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {isLoading
              ? "Läser in"
              : `Visar ${filteredFacilities.length} ${
                  filteredFacilities.length === 1 ? "anläggning" : "anläggningar"
                }${hasActiveQuery ? ` av ${facilities.length}` : ""}`}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: LINE }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {isLoading ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    px: 3,
                    py: 2.5,
                    borderBottom: i < 3 ? `1px solid ${LINE_SOFT}` : "none",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={48}
                    height={48}
                    sx={{ borderRadius: "14px" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="38%" height={20} />
                    <Skeleton width="24%" height={14} sx={{ mt: 0.5 }} />
                  </Box>
                  <Skeleton variant="rounded" width={110} height={24} sx={{ borderRadius: "999px" }} />
                  <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: "999px" }} />
                </Box>
              ))}
            </Box>
          ) : filteredFacilities.length === 0 ? (
            <EmptyState
              onCreate={() => setIsCreateModalOpen(true)}
              hasQuery={hasActiveQuery}
            />
          ) : viewMode === "list" ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(10,22,40,0.02)",
                "& > *:last-child": { borderBottom: "none" },
              }}
            >
              {filteredFacilities.map((facility) => (
                <FacilityRow
                  key={facility.id}
                  facility={facility}
                  onSelect={setEditFacilityId}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2.5,
              }}
            >
              {filteredFacilities.map((facility) => (
                <FacilityCard
                  key={facility.id}
                  facility={facility}
                  onSelect={setEditFacilityId}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        title="Lägg till anläggning"
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      >
        <CreateSubjectModal
          customerid={organisationId}
          closeCreateModal={() => setIsCreateModalOpen(false)}
        />
      </Dialog>
      {editFacilityId && (
        <Dialog
          title="Redigera anläggning"
          open={!!editFacilityId}
          onClose={closeEditModal}
        >
          <FacilityEditModal
            facilityId={editFacilityId}
            onClose={closeEditModal}
          />
        </Dialog>
      )}
    </Content>
  );
};

export default FacilityView;
